import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MessageContent, Thread } from '../../interface';
import parseISO from 'date-fns/parseISO';
import format from 'date-fns/format';
import { TranslocoService, TranslocoModule } from '@ngneat/transloco';
import { SkyI18nModule, registerTranslationsManuallyStatic } from '@skyeng/libs/shared/i18n';
import { Attachment, SkyproMessagesModule } from '@skyeng/libs/skypro/messages';
import { Store } from '@skyeng/libs/shared/store';
import { map } from 'rxjs';
import { ViewportSizes, ViewportSizeService, HighlightCodeDirective } from '@skyeng/libs/skypro/misc';
import { CommunicationLikeDirective } from '../communication-like/communication-like.directive';
import { convertHtmlListToParagraphs } from '../../helpers';
import { NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { QuillModule } from 'ngx-quill';
import { MatMenuModule } from '@angular/material/menu';
import { VimCoreUserModule } from '@skyeng/libs/vimbox-core/user';
import { DsIconModule } from '@skyeng/libs/shared/ui-kit-v2/icon';
import entranslation from '../../translation/en.json';
import rutranslation from '../../translation/ru.json';

const MAX_PREVIEW_HEIGHT = 110;
const MAX_COMPACT_PREVIEW_HEIGHT = 44;

@Component({
  selector: 'skypro-communication-message',
  templateUrl: 'communication-message.html',
  styleUrls: ['communication-message.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SkyI18nModule,
    VimCoreUserModule,
    DsIconModule,
    MatMenuModule,
    QuillModule,
    SkyproMessagesModule,
    NgTemplateOutlet,
    AsyncPipe,
    TranslocoModule,
    HighlightCodeDirective,
  ],
})
export class CommunicationsMessageComponent implements AfterViewInit {
  @Input() message: MessageContent;
  @Input() isActive = false;
  @Input() isDeleted = false;
  @Input() isPinned = false;
  @Input() isHidden = false;
  @Input() canEdit = false;
  @Input() canDelete = false;
  @Input() canClose = false;
  @Input() canPin = false;
  @Input() canHide = false;
  @Input() isCompactPreview = false;
  @Input() hasActions = true;

  @Output() edit = new EventEmitter<void>();
  @Output() delete = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() pin = new EventEmitter<void>();
  @Output() unpin = new EventEmitter<void>();
  @Output() hide = new EventEmitter<void>();
  @Output() show = new EventEmitter<void>();

  @ContentChild(CommunicationLikeDirective) content!: CommunicationLikeDirective;

  private translocoService = inject(TranslocoService);
  private viewportSizeService = inject(ViewportSizeService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  store = new Store({
    isPreview: true,
    needShowPreviewToggler: true,
  });

  isPreview$ = this.store.select('isPreview');
  needShowPreviewToggler$ = this.store.select('needShowPreviewToggler');
  isMobile$ = this.viewportSizeService.viewport$.pipe(
    map(viewport => {
      switch (viewport) {
        case ViewportSizes.m:
        case ViewportSizes.l:
        case ViewportSizes.xl:
          return false;
        default:
          return true;
      }
    }),
  );

  @ViewChild('messageBodyRef', { read: ElementRef }) messageBodyRef: ElementRef<HTMLElement>;

  constructor() {
    registerTranslationsManuallyStatic('skyproCommunications', this.translocoService, {
      en: entranslation,
      ru: rutranslation,
    });
  }

  ngAfterViewInit(): void {
    if (!this.isDeleted) {
      this.checkIfNeedShowToggler();
    }
  }

  formatTime(): string {
    const { createdAt } = this.message;

    // TODO: use pipe
    const localDate = parseISO(createdAt);
    const formatedDate = format(localDate, 'dd.MM.yy HH:mm');

    return formatedDate;
  }

  trackByAttachment(_index: number, attachment: Attachment): string {
    return attachment.url;
  }

  editMessage(): void {
    this.edit.emit();
  }

  deleteMessage(): void {
    const message = 'Вы уверены что хотите удалить это сообщение? Удаление нельзя будет отменить.';
    const isConfirmed = confirm(message);
    if (!isConfirmed) {
      return;
    }

    this.delete.emit();
  }

  pinMessage(): void {
    this.pin.emit();
  }

  unpinMessage(): void {
    this.unpin.emit();
  }

  hideMessage(): void {
    this.hide.emit();
  }

  showMessage(): void {
    this.show.emit();
  }

  checkIfNeedShowToggler(): void {
    const element = this.messageBodyRef.nativeElement?.querySelector('.ql-editor') as HTMLElement | null;

    if (!element) {
      return;
    }

    // сначала обрабатываем наличие инлайн-картинок, иначе они могут не уйдти под кат
    const images = element.querySelectorAll('img');

    if (images.length > 0) {
      this.store.updateField('needShowPreviewToggler', false);
      this.store.updateField('isPreview', false);

      this.processMessageHeightWhenImagesLoad(images, element);
      return;
    }

    // Делаем небольшую поправку на левые отступы, которые невидимы глазу, но которые тригерять появление "сворачивалки"
    const SCROLL_FIXER_SIZE = 2;
    // offsetHeight - высота отрендеренного кусочка обрезанного (если оно обрезано) сообщения, scrollHeight - высота полного сообщения
    const isMessageOverflowHeight = element.offsetHeight + SCROLL_FIXER_SIZE < element.scrollHeight;
    const isMessageOverflowWidth = element.offsetWidth + SCROLL_FIXER_SIZE < element.scrollWidth;
    const isMessageOverflowParent = isMessageOverflowHeight || isMessageOverflowWidth;

    if (isMessageOverflowParent) {
      return;
    }

    this.store.updateField('needShowPreviewToggler', false);
    this.store.updateField('isPreview', false);

    // Известная проблема: при перерисовке сообщения, поле needShowPreviewToggler выставляется в false тут в коде, но в шаблоне почему то не обновляется
    // Обсуждение: https://mm-time.skyeng.tech/skyeng/pl/do8rmizbpjfo5xr8fz4u67fbsh
    // Это происходит из-за того что этот метод вызывается в AfterViewInit, а изменения компонента внутри AfterViewInit не вызывают перерисовку шаблона
    this.changeDetectorRef.detectChanges();
  }

  private processMessageHeightWhenImagesLoad(images: NodeListOf<HTMLImageElement>, element: HTMLElement): void {
    images.forEach(img => {
      img.addEventListener('load', () => {
        if (
          element.scrollHeight > MAX_PREVIEW_HEIGHT ||
          (this.isCompactPreview && element.scrollHeight > MAX_COMPACT_PREVIEW_HEIGHT)
        ) {
          this.store.updateField('needShowPreviewToggler', true);
          this.store.updateField('isPreview', true);
        }
      });
    });
  }

  changePreviewMode(): void {
    const isPreview = this.store.get('isPreview');
    this.store.updateField('isPreview', !isPreview);
  }

  getFlatMessageBody(messageBody: string): string {
    return convertHtmlListToParagraphs(messageBody);
  }

  getMessageContentType(content: MessageContent): string {
    const isThread = (content as Thread).status !== undefined;
    return isThread ? 'thread' : 'message';
  }
}
